import React from 'react';
import { Chip, makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CancelIcon from '@material-ui/icons/Cancel';
import PauseIcon from '@material-ui/icons/Pause';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { TermsDocument } from 'lib/Model/TermsGenerator';

// Best status first: when a brand has many terms, the first match decides coverage
export const TERMS_STATUSES = [
  'Active',
  'Paused',
  'Not yet in effect',
  'Pending Signature',
  'Expired',
  'Cancelled',
];

// One status rule for every screen that shows terms, so they cannot disagree.
// get_all_terms.sql and get_latest_terms_status_for_company.sql (intranet-api)
// and knt_tob_expiry.sql (exp42-etl commercial_proact) copy this rule.
export function getTermsStatus(term: TermsDocument): string {
  const today = new Date();

  if (term.is_cancelled) {
    return 'Cancelled';
  }
  if (!term.signature_date) {
    return 'Pending Signature';
  }
  // Compare calendar days: the expiry date is the last valid day
  if (
    term.terms_expiry_date &&
    differenceInCalendarDays(parseISO(term.terms_expiry_date), today) < 0
  ) {
    return 'Expired';
  }
  if (differenceInCalendarDays(parseISO(term.signature_date), today) > 0) {
    return 'Not yet in effect';
  }
  if (term.is_paused) {
    return 'Paused';
  }
  return 'Active';
}

const useStyles = makeStyles((theme) => ({
  chip: {
    minWidth: '90px',
    fontWeight: 'bold',
  },
  active: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  expired: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.text.primary,
  },
  pending: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  cancelled: {
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },
  paused: {
    backgroundColor: theme.palette.warning.dark,
    color: theme.palette.info.contrastText,
  },
  icon: {
    fontSize: '1rem',
    // Chips set their own icon colour; match the label instead
    '&&': {
      color: 'inherit',
    },
  },
}));

export const TermsStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const classes = useStyles();

  let colourClass = classes.pending;
  let icon = <HourglassEmptyIcon className={classes.icon} />;
  if (status === 'Active') {
    colourClass = classes.active;
    icon = <CheckCircleIcon className={classes.icon} />;
  } else if (status === 'Expired') {
    colourClass = classes.expired;
    icon = <ErrorIcon className={classes.icon} />;
  } else if (status === 'Paused') {
    colourClass = classes.paused;
    icon = <PauseIcon className={classes.icon} />;
  } else if (status === 'Cancelled') {
    colourClass = classes.cancelled;
    icon = <CancelIcon className={classes.icon} />;
  }

  return (
    <Chip
      label={status}
      size="small"
      icon={icon}
      className={`${classes.chip} ${colourClass}`}
    />
  );
};
